import { CloseIcon } from "@chakra-ui/icons";
import {
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { memo, useEffect } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import { darkMode, lightMode } from "../../../lib/models/color-mode-colors";
import { SelectItem } from "../../../lib/types/SelectItem";

export interface OptionInputProps {
  label: string;
  selectedItem: SelectItem | null;
  inputRef: React.RefObject<HTMLInputElement>;
  showClearButton: Boolean;
  handleInputClick: () => void;
  handleInputBlur: () => void;
  handleClearSelection: () => void;
  handleInputChange: (query: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

function OptionInput(props: OptionInputProps) {
  const {
    label,
    selectedItem,
    inputRef,
    showClearButton,
    handleInputClick,
    handleInputBlur,
    handleClearSelection,
    handleInputChange,
    placeholder = "Select...",
    isDisabled = false,
  } = props;

  const [inputValue, setInputValue] = React.useState<string>("");

  const debouncedInput = useDebounce(inputValue, 500);

  const bgColor = useColorModeValue(lightMode[100], darkMode[200]);

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    handleInputChange(debouncedInput);
  }, [debouncedInput, handleInputChange]);

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <InputGroup>
        <Input
          ref={inputRef}
          onClick={handleInputClick}
          onFocus={handleFocus}
          onBlur={handleInputBlur}
          isDisabled={isDisabled}
          type="text"
          placeholder={selectedItem?.label ?? placeholder}
          value={selectedItem ? "" : inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          background={bgColor}
        />
        {selectedItem && (
          <InputRightElement>
            {showClearButton && (
              <IconButton
                size="xs"
                aria-label="Clear selection"
                onClick={handleClearSelection}
                icon={<CloseIcon h={2} w={2} />}
              />
            )}
          </InputRightElement>
        )}
      </InputGroup>
    </FormControl>
  );
}

export default memo(OptionInput);
